import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { WIDGET_HOST_CHANGED_EVENT } from 'utils/widgets/widgetHost.utils';
import { isLeafletMapUsable, stopLeafletMap } from '../util/BaseMap.utils';

/**
 * Helper component to fix tile loading issues by calling invalidateSize
 * after the map mounts and on window resize.
 */
const MapInvalidateSizeFix = () => {
  const map = useMap();

  useEffect(() => {
    let unmounted = false; // track whether the component has been unmounted
    let frameId = null;
    let frameWindow = null;
    let resizeObserver = null;
    let settleTimers = [];
    let runtimeWindow = null;
    const container = map?.getContainer();

    const markUnmounted = () => {
      unmounted = true;
    };

    const invalidate = () => {
      if (unmounted) return;
      if (!isLeafletMapUsable(map)) return;
      stopLeafletMap(map);
      map.invalidateSize({ animate: false, pan: false }); // Do not re-center while simply resizing the map container.
    };

    const scheduleInvalidate = () => {
      if (unmounted) return;
      if (frameId !== null) {
        frameWindow?.cancelAnimationFrame(frameId);
      }
      frameWindow = runtimeWindow || container?.ownerDocument?.defaultView || window;
      frameId = frameWindow.requestAnimationFrame(() => {
        frameId = null;
        invalidate();
      });
    };

    const scheduleSettledInvalidates = () => {
      settleTimers.forEach(({ id, ownerWindow }) => ownerWindow.clearTimeout(id));
      const timerWindow = runtimeWindow || container?.ownerDocument?.defaultView || window;
      settleTimers = [40, 120, 260].map((delay) => ({
        id: timerWindow.setTimeout(() => {
          scheduleInvalidate();
        }, delay),
        ownerWindow: timerWindow,
      }));
    };

    const handleResize = () => {
      if (unmounted) return;
      scheduleInvalidate();
      scheduleSettledInvalidates();
    };

    const bindRuntimeObservers = () => {
      runtimeWindow?.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();

      runtimeWindow = container?.ownerDocument?.defaultView || window;
      runtimeWindow.addEventListener('resize', handleResize);

      const RuntimeResizeObserver = runtimeWindow.ResizeObserver;
      if (container && typeof RuntimeResizeObserver === 'function') {
        resizeObserver = new RuntimeResizeObserver(scheduleInvalidate);
        resizeObserver.observe(container);
      } else {
        resizeObserver = null;
      }
    };

    const handleHostChanged = () => {
      bindRuntimeObservers();
      handleResize();
    };

    map.on('unload', markUnmounted); // Starts listening for the map unload event to mark the component as unmounted

    // Small delay to ensure container is fully rendered
    bindRuntimeObservers();
    if (container) {
      container.dataset.widgetHostAware = '';
    }
    container?.addEventListener(WIDGET_HOST_CHANGED_EVENT, handleHostChanged);
    const timeoutWindow = runtimeWindow;
    const timeout = timeoutWindow.setTimeout(() => {
      scheduleInvalidate();
      scheduleSettledInvalidates();
    }, 100);

    return () => {
      unmounted = true;
      map.off('unload', markUnmounted);
      timeoutWindow.clearTimeout(timeout);
      settleTimers.forEach(({ id, ownerWindow }) => ownerWindow.clearTimeout(id));
      if (frameId !== null) {
        frameWindow?.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      runtimeWindow?.removeEventListener('resize', handleResize);
      container?.removeEventListener(WIDGET_HOST_CHANGED_EVENT, handleHostChanged);
      if (container) {
        delete container.dataset.widgetHostAware;
      }
    };
  }, [map]);

  return null;
};

export default MapInvalidateSizeFix;
